
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingPage } from './LoadingPage';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isAdmin, isLoading, session } = useAuth();

  // Show loading while authentication state is being determined
  if (isLoading) {
    return <LoadingPage />;
  }

  // Comprehensive security checks:
  // 1. Must have authenticated user
  // 2. Must have valid session
  // 3. Must have admin role (from AuthContext which checks both sources)
  // 4. User ID must match session user ID (prevent session hijacking)
  const isSecureAdmin = Boolean(
    user &&
    session &&
    isAdmin &&
    user.id === session.user?.id
  );

  if (!isSecureAdmin) {
    // Only show debug info in development
    if (import.meta.env.DEV) {
      console.warn('AdminRoute: Access denied', {
        hasUser: !!user,
        hasSession: !!session,
        isAdmin,
        userIdMatch: user?.id === session?.user?.id,
        userEmail: user?.email,
        debugInfo: 'Check AuthContext isAdmin value - should be true for admin users'
      });

      if (user?.email) {
        console.error(`❌ Admin access denied for ${user.email}`);
        console.error(`🔧 AuthContext isAdmin: ${isAdmin}`);
        console.error(`🔧 User ID match: ${user?.id === session?.user?.id}`);
      }
    }

    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
